import {
  type CallhomeCamera,
  type CallhomeSiteWithRelationalData,
  type DeviceEntity,
  type SiteId,
} from '@solinkcorp/api-types';
import { getSiteKey, useSite } from '@solinkcorp/callhome-data';
import { useQueryParams, useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import { type Immutable } from 'immer';
import React, { useCallback, useState } from 'react';
import {
  DEFAULT_LIVE_FEED_ROTATION_SECONDS,
  LIVE_FEED_CAMERA_STATUS_REFRESH_INTERVAL_MS,
} from '../../../constants';
import {
  useSdmLivePlayer,
  type SdmLivePlayerComponent,
  type SdmLivePlayerErrorCode,
  type SdmLivePlayerState,
} from '../../../contexts/LiveVideoContext';
import useCamerasForAiTags from '../../../hooks/useCamerasForAiTags';
import { type SDMLiveFeedWidget } from '../../../layout/types';
import { type CommonWidgetProps } from '../../../types';
import WidgetCard from '../WidgetCard';
import FeedCarouselDots from './FeedCarouselDots';
import { selectLiveFeedError, toLiveFeedErrorCode } from './liveFeedError';
import LiveFeedOverlay from './LiveFeedOverlay';
import LiveFeedStage from './LiveFeedStage';
import { useFeedRotation } from './useFeedRotation';
import { useLiveFeedTelemetry } from './useLiveFeedTelemetry';

interface PlayerReport {
  state: SdmLivePlayerState;
  errorCode: SdmLivePlayerErrorCode | undefined;
}

interface LiveFeedSlotProps {
  Player: SdmLivePlayerComponent;
  camera: CallhomeCamera;
  device: DeviceEntity;
  site: CallhomeSiteWithRelationalData;
  /** Off-screen slots stay mounted and streaming so they are ready the moment they rotate in. */
  hidden: boolean;
  onStateChange: (
    cameraId: string,
    state: SdmLivePlayerState,
    errorCode?: SdmLivePlayerErrorCode,
  ) => void;
  onStreamChange: (cameraId: string, streamId: string | null) => void;
  onVideoElement: (cameraId: string, element: HTMLVideoElement | null) => void;
}

/** One camera's player, reporting its state and video element under that camera's id. */
const LiveFeedSlot: React.FC<LiveFeedSlotProps> = ({
  Player,
  camera,
  device,
  site,
  hidden,
  onStateChange,
  onStreamChange,
  onVideoElement,
}) => {
  const cameraId = camera.id;
  const handleStateChange = useCallback(
    (state: SdmLivePlayerState, errorCode?: SdmLivePlayerErrorCode) =>
      onStateChange(cameraId, state, errorCode),
    [cameraId, onStateChange],
  );
  const handleStreamChange = useCallback(
    (streamId: string | null) => onStreamChange(cameraId, streamId),
    [cameraId, onStreamChange],
  );
  const handleVideoElement = useCallback(
    (element: HTMLVideoElement | null) => onVideoElement(cameraId, element),
    [cameraId, onVideoElement],
  );

  return (
    <div
      className="absolute inset-0"
      style={{ visibility: hidden ? 'hidden' : 'visible' }}
      data-testid="sdm-live-feed-slot"
      data-camera-id={cameraId}
      data-hidden={hidden}
    >
      <Player
        camera={camera}
        device={device}
        site={site}
        onStateChange={handleStateChange}
        onStreamChange={handleStreamChange}
        onVideoElement={handleVideoElement}
      />
    </div>
  );
};

interface LiveFeedWidgetProps extends CommonWidgetProps {
  config: Immutable<SDMLiveFeedWidget>;
}

/**
 * Shows a live feed for every camera whose zones or lines carry one of the widget's AI tags,
 * rotating between them when more than one matches.
 */
const LiveFeedWidget: React.FC<LiveFeedWidgetProps> = (props) => {
  const { widget, config } = props;
  const [query] = useQueryParams();
  const siteId = (query.get('siteId') ?? undefined) as SiteId | undefined;
  // The site is cached without revalidation, so camera status would never change after the first
  // fetch. Polling the shared site key refreshes every consumer of the site.
  const staleSiteConfig = useStaleSWRConfig(siteId ? getSiteKey(siteId) : null);
  const { site } = useSite({
    siteId,
    config: {
      ...staleSiteConfig,
      refreshInterval: LIVE_FEED_CAMERA_STATUS_REFRESH_INTERVAL_MS,
      refreshWhenHidden: true,
    },
  });
  const device = site?.devices?.[0];

  const { cameras, isLoaded: camerasLoaded } = useCamerasForAiTags(siteId, widget.tagIds);
  const Player = useSdmLivePlayer();

  const { activeIndex, nextIndex } = useFeedRotation(
    cameras.length,
    config.rotationSeconds ?? DEFAULT_LIVE_FEED_ROTATION_SECONDS,
  );
  const activeCamera = cameras[activeIndex];
  const nextCamera = nextIndex === activeIndex ? undefined : cameras[nextIndex];

  // Keyed by camera id because slots stay mounted across rotations; reading the active camera's
  // entry avoids any dependence on the order in which slots mount and unmount.
  const [playerReports, setPlayerReports] = useState<Record<string, PlayerReport>>({});
  const [streamIds, setStreamIds] = useState<Record<string, string | null>>({});
  const [videoElements, setVideoElements] = useState<Record<string, HTMLVideoElement | null>>({});

  const handleStateChange = useCallback(
    (cameraId: string, state: SdmLivePlayerState, errorCode?: SdmLivePlayerErrorCode) => {
      setPlayerReports((prev) =>
        prev[cameraId]?.state === state && prev[cameraId]?.errorCode === errorCode
          ? prev
          : { ...prev, [cameraId]: { state, errorCode } },
      );
    },
    [],
  );

  const handleStreamChange = useCallback((cameraId: string, streamId: string | null) => {
    setStreamIds((prev) =>
      prev[cameraId] === streamId ? prev : { ...prev, [cameraId]: streamId },
    );
  }, []);

  const handleVideoElement = useCallback((cameraId: string, element: HTMLVideoElement | null) => {
    setVideoElements((prev) =>
      prev[cameraId] === element ? prev : { ...prev, [cameraId]: element },
    );
  }, []);

  const playback = Player && site && device ? { Player, site, device } : null;

  const activeReport = activeCamera ? playerReports[activeCamera.id] : undefined;
  const activeVideoElement = activeCamera ? (videoElements[activeCamera.id] ?? null) : null;

  const error = selectLiveFeedError({
    camerasLoaded,
    activeCamera,
    canPlay: !!playback,
    playerState: activeReport?.state,
  });

  const errorCode = toLiveFeedErrorCode({
    error,
    canPlay: !!playback,
    playerErrorCode: activeReport?.errorCode,
  });

  useLiveFeedTelemetry(
    widget.id,
    {
      activeCameraId: activeCamera?.id ?? null,
      cameraCount: cameras.length,
      streamId: activeCamera ? (streamIds[activeCamera.id] ?? null) : null,
      state: errorCode ? 'error' : (activeReport?.state ?? 'connecting'),
      errorCode,
    },
    activeVideoElement,
  );

  // Mount a player for every online camera we're showing or about to show. This deliberately
  // ignores `error`: a player that failed has to stay mounted for its own retry and HLS fallback
  // to recover it, and its overlay clears as soon as it reports playing again.
  const slotCameras = playback
    ? [activeCamera, nextCamera].filter(
        (camera): camera is CallhomeCamera => camera?.status === 'online',
      )
    : [];

  // The grid's `isDataLoading` tracks the historical events fetch, which this widget does not use.
  // The skeleton follows the camera lookup instead.
  const { isDataLoading, ...widgetCardProps } = props;

  return (
    <WidgetCard
      {...widgetCardProps}
      isDataLoading={!camerasLoaded}
      level="normal"
      title={widget.title}
      overlayChrome
    >
      <LiveFeedStage
        videoElement={activeVideoElement}
        overlay={error ? <LiveFeedOverlay error={error} /> : null}
        chrome={<FeedCarouselDots count={cameras.length} activeIndex={activeIndex} />}
      >
        {playback &&
          slotCameras.map((camera) => (
            <LiveFeedSlot
              key={camera.id}
              Player={playback.Player}
              camera={camera}
              device={playback.device}
              site={playback.site}
              hidden={camera.id !== activeCamera?.id}
              onStateChange={handleStateChange}
              onStreamChange={handleStreamChange}
              onVideoElement={handleVideoElement}
            />
          ))}
      </LiveFeedStage>
    </WidgetCard>
  );
};

export default React.memo(LiveFeedWidget);
